import { useEffect, useRef, useCallback } from "react";
import PreviewRating from "../preview/preview-rating";
import PreviewWelcome from "../preview/preview-welcome";
import PreviewNegativeFeedback from "../preview/preview-negative-feedback";
import PreviewQuestion from "../preview/preview-question";
import PreviewPersonalDetail from "../preview/preview-personal-detail";
import PreviewCompanyDetail from "../preview/preview-company-detail";
import PreviewConsent from "../preview/preview-consent";
import PreviewThankYou from "../preview/preview-thank-you";
import PreviewFeedbackThankYou from "../preview/preview-feedback-thank-you";
import type { useFormBuilderState } from "./hooks/use-form-builder-state";
import type { BrandKit } from "@/modules/brand/types";

// Track loaded fonts to avoid duplicate loading
const loadedFonts = new Set<string>();

function loadGoogleFont(fontName: string, weights: string[] = ["300", "400", "500", "600", "700", "800"]) {
	const fontKey = `${fontName}:${weights.join(",")}`;
	if (loadedFonts.has(fontKey)) return;

	const linkId = `google-font-${fontName.replace(/\s+/g, "-").toLowerCase()}`;
	if (document.getElementById(linkId)) {
		loadedFonts.add(fontKey);
		return;
	}

	const link = document.createElement("link");
	link.id = linkId;
	link.rel = "stylesheet";
	link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@${weights.join(";")}&display=swap`;
	document.head.appendChild(link);
	loadedFonts.add(fontKey);
}

interface FormBuilderPreviewProps {
	formState: ReturnType<typeof useFormBuilderState>;
	brandName: string;
	brandLogo: string | null;
	brandKit?: BrandKit | null;
	isDarkMode?: boolean;
}

// Wrapper component for full-screen preview pages
function PreviewPage({ id, brandKit, isDarkMode, children }: { id: string; brandKit?: BrandKit | null; isDarkMode?: boolean; children: React.ReactNode }) {
	// Apply brand colors as CSS custom properties
	const style = brandKit
		? ({
				"--color-brand": brandKit.color_primary_hex || "#432dd7",
				"--color-accent": brandKit.color_accent_hex || "#e17100",
				"--font-heading": `"${brandKit.font_heading_name || "Inter"}", sans-serif`,
				"--font-heading-weight": brandKit.font_heading_weight || "700",
				"--font-body": `"${brandKit.font_body_name || "Inter"}", sans-serif`,
				"--font-body-weight": brandKit.font_body_weight || "400",
			} as React.CSSProperties)
		: {};

	return (
		<div
			id={id}
			data-section-id={id.replace("preview-", "")}
			className={`h-[calc(100vh-142px)] flex items-center justify-center snap-start snap-always ${isDarkMode ? "bg-gray-950" : "bg-[#fafafa]"}`}
			style={style}
		>
			<div className="w-full h-full">{children}</div>
		</div>
	);
}

export default function FormBuilderPreview({ formState, brandName, brandLogo, brandKit, isDarkMode = false }: FormBuilderPreviewProps) {
	const { sections, questions, selectedSectionId, setSelectedSectionId, updateSectionTitle, updateSectionDescription, updateSectionField, updateQuestion } = formState;
	const containerRef = useRef<HTMLDivElement>(null);
	const isScrollingRef = useRef(false);

	// Load Google Fonts when brandKit changes
	useEffect(() => {
		if (brandKit) {
			if (brandKit.font_heading_name) {
				loadGoogleFont(brandKit.font_heading_name);
			}
			if (brandKit.font_body_name) {
				loadGoogleFont(brandKit.font_body_name);
			}
		}
	}, [brandKit]);

	// Get sections by type
	const ratingSection = sections.find((s) => s.type === "rating");
	const welcomeSection = sections.find((s) => s.type === "welcome");
	const personalDetailSection = sections.find((s) => s.type === "personal-detail");
	const companyDetailSection = sections.find((s) => s.type === "company-detail");
	const consentSection = sections.find((s) => s.type === "consent");
	const thankYouSection = sections.find((s) => s.type === "thank-you");
	const negativeFeedbackSection = sections.find((s) => s.type === "negative-feedback");
	const feedbackThankYouSection = sections.find((s) => s.type === "feedback-thank-you");

	// Handle scroll-based section detection
	const handleSectionInView = useCallback(
		(sectionId: string) => {
			// Only update if not programmatically scrolling
			if (!isScrollingRef.current) {
				setSelectedSectionId(sectionId);
			}
		},
		[setSelectedSectionId],
	);

	// Set up Intersection Observer
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
						const sectionId = entry.target.getAttribute("data-section-id");
						if (sectionId) {
							handleSectionInView(sectionId);
						}
					}
				});
			},
			{
				root: container,
				threshold: 0.5,
			},
		);

		// Observe all preview pages
		const previewPages = container.querySelectorAll("[data-section-id]");
		previewPages.forEach((page) => observer.observe(page));

		return () => observer.disconnect();
	}, [handleSectionInView, sections, questions]);

	// Handle programmatic scrolling (from left panel clicks)
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const handleScrollEnd = () => {
			// Reset after scroll completes
			setTimeout(() => {
				isScrollingRef.current = false;
			}, 100);
		};

		container.addEventListener("scrollend", handleScrollEnd);

		return () => {
			container.removeEventListener("scrollend", handleScrollEnd);
		};
	}, []);

	if (!brandKit) {
		return null;
	}

	return (
		<div ref={containerRef} className="h-[calc(100vh-142px)] overflow-y-auto snap-y snap-mandatory">
			{/* Rating section */}
			{ratingSection && (
				<PreviewPage id={`preview-${ratingSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewRating
						id={`preview-content-${ratingSection.id}`}
						section={ratingSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === ratingSection.id}
						onTitleChange={(title) => updateSectionTitle(ratingSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(ratingSection.id, desc)}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}

			{/* Welcome section - only if enabled */}
			{welcomeSection && welcomeSection.enabled && (
				<PreviewPage id={`preview-${welcomeSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewWelcome
						id={`preview-content-${welcomeSection.id}`}
						section={welcomeSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === welcomeSection.id}
						onTitleChange={(title) => updateSectionTitle(welcomeSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(welcomeSection.id, desc)}
						questionCount={questions.length}
						questionFormats={questions.map((q) => q.format)}
						companyDetailEnabled={companyDetailSection?.enabled ?? false}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}

			{/* Questions */}
			{questions.map((question, index) => (
				<PreviewPage key={question.id} id={`preview-${question.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewQuestion
						id={`preview-content-${question.id}`}
						question={question}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						questionIndex={index}
						totalQuestions={questions.length}
						isSelected={selectedSectionId === question.id}
						onTitleChange={(title) => updateQuestion(question.id, { title })}
						onDescriptionChange={(desc) => updateQuestion(question.id, { description: desc })}
						onVideoOptionLabelChange={(val) => updateQuestion(question.id, { video_option_label: val })}
						onVideoOptionDescriptionChange={(val) => updateQuestion(question.id, { video_option_description: val })}
						onAudioOptionLabelChange={(val) => updateQuestion(question.id, { audio_option_label: val })}
						onAudioOptionDescriptionChange={(val) => updateQuestion(question.id, { audio_option_description: val })}
						onTextOptionLabelChange={(val) => updateQuestion(question.id, { text_option_label: val })}
						onTextOptionDescriptionChange={(val) => updateQuestion(question.id, { text_option_description: val })}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			))}

			{/* Personal detail */}
			{personalDetailSection && (
				<PreviewPage id={`preview-${personalDetailSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewPersonalDetail
						id={`preview-content-${personalDetailSection.id}`}
						section={personalDetailSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === personalDetailSection.id}
						onTitleChange={(title) => updateSectionTitle(personalDetailSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(personalDetailSection.id, desc)}
						onFieldUpdate={(fieldId, updates) => updateSectionField(personalDetailSection.id, fieldId, updates)}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}

			{/* Company detail - only if enabled */}
			{companyDetailSection && companyDetailSection.enabled && (
				<PreviewPage id={`preview-${companyDetailSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewCompanyDetail
						id={`preview-content-${companyDetailSection.id}`}
						section={companyDetailSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === companyDetailSection.id}
						onTitleChange={(title) => updateSectionTitle(companyDetailSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(companyDetailSection.id, desc)}
						onFieldUpdate={(fieldId, updates) => updateSectionField(companyDetailSection.id, fieldId, updates)}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}

			{/* Consent - only if enabled */}
			{consentSection && consentSection.enabled && (
				<PreviewPage id={`preview-${consentSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewConsent
						id={`preview-content-${consentSection.id}`}
						section={consentSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === consentSection.id}
						onTitleChange={(title) => updateSectionTitle(consentSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(consentSection.id, desc)}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}

			{/* Thank you */}
			{thankYouSection && (
				<PreviewPage id={`preview-${thankYouSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewThankYou
						id={`preview-content-${thankYouSection.id}`}
						section={thankYouSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === thankYouSection.id}
						onTitleChange={(title) => updateSectionTitle(thankYouSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(thankYouSection.id, desc)}
						onImageUrlChange={(imageUrl) => formState.updateSectionImageUrl(thankYouSection.id, imageUrl)}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}

			{/* Negative feedback */}
			{negativeFeedbackSection && (
				<PreviewPage id={`preview-${negativeFeedbackSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewNegativeFeedback
						id={`preview-content-${negativeFeedbackSection.id}`}
						section={negativeFeedbackSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === negativeFeedbackSection.id}
						onTitleChange={(title) => updateSectionTitle(negativeFeedbackSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(negativeFeedbackSection.id, desc)}
						onFieldUpdate={(fieldId, updates) => updateSectionField(negativeFeedbackSection.id, fieldId, updates)}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}

			{/* Feedback Thank you */}
			{feedbackThankYouSection && (
				<PreviewPage id={`preview-${feedbackThankYouSection.id}`} brandKit={brandKit} isDarkMode={isDarkMode}>
					<PreviewFeedbackThankYou
						id={`preview-content-${feedbackThankYouSection.id}`}
						section={feedbackThankYouSection}
						brandName={brandName}
						brandLogo={brandLogo}
						brandkit={brandKit}
						isSelected={selectedSectionId === feedbackThankYouSection.id}
						onTitleChange={(title) => updateSectionTitle(feedbackThankYouSection.id, title)}
						onDescriptionChange={(desc) => updateSectionDescription(feedbackThankYouSection.id, desc)}
						onImageUrlChange={(imageUrl) => formState.updateSectionImageUrl(feedbackThankYouSection.id, imageUrl)}
						isDarkMode={isDarkMode}
					/>
				</PreviewPage>
			)}
		</div>
	);
}
